import React, { useCallback, useState } from 'react';
import { ActionIcon, Box, Divider, Group, Stack, Text, ThemeIcon, Button } from '@mantine/core';
import {
  IconBuilding,
  IconChartBar,
  IconChevronRight,
  IconFolderOpen,
  IconLayoutDashboard,
  IconMenu2,
  IconX, IconArrowLeft
} from '@tabler/icons-react';
import { NavLink, Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { realEstateApi } from '../../api/apiRealEstate';
import '../../index.css';

const PAGES = [
  { path: '/gayrimenkul/dashboard', id: 'dashboard', title: 'Dashboard', subtitle: 'Portföy özeti', icon: IconLayoutDashboard },
  { path: '/gayrimenkul/properties', id: 'properties', title: 'Gayrimenkuller', subtitle: 'Mülk ve parsel kayıtları', icon: IconBuilding },
  { path: '/gayrimenkul/documents', id: 'documents', title: 'Evrak Yönetimi', subtitle: 'Belge arşivi', icon: IconFolderOpen },
  { path: '/gayrimenkul/reports', id: 'reports', title: 'Raporlar', subtitle: 'Envanter analizi', icon: IconChartBar },
];

const PATH_BY_SECTION = Object.fromEntries(PAGES.map((p) => [p.id, p.path]));

/**
 * Gayrimenkul modülünün ortak görünümü.
 * App.js: <ProtectedRoute><RealEstateLayout /></ProtectedRoute>
 * Dört bağımsız sayfa: <Outlet /> üzerinden gösterilir.
 * API ve kullanıcı rolü App.js'ten prop olarak alınmaz.
 */
export default function RealEstateLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  const onMutated = useCallback(() => setRefreshKey((value) => value + 1), []);
  const onNavigate = useCallback((section) => {
    const path = PATH_BY_SECTION[section];
    if (path) navigate(path);
    setMobileOpen(false);
  }, [navigate]);

  const role = typeof window === 'undefined' ? '' : window.localStorage.getItem('user_role');
  const canEdit = ['admin', 'yönetici'].includes(String(role ?? '').toLocaleLowerCase('tr-TR'));
  const current = PAGES.find((p) => p.path === location.pathname) || PAGES[0];

  return (
    <Box className="re-shell">
      <aside className={`re-sidebar ${mobileOpen ? 're-mobile-open' : ''}`}>
        <Group gap="sm" px="lg" pt="xl" pb="lg" justify="space-between">
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon color="blue" variant="filled" size={40} radius="md">
              <IconBuilding size={23} />
            </ThemeIcon>
            <Box>
              <Text size="xs" fw={800} c="dimmed" style={{ letterSpacing: '.08em' }}>SMS GRUP PORTAL</Text>
              <Text fw={700} size="md">Gayrimenkul</Text>
            </Box>
          </Group>
          <ActionIcon aria-label="Menüyü kapat" className="re-mobile-close" variant="subtle" onClick={() => setMobileOpen(false)}>
            <IconX size={20} />
          </ActionIcon>
        </Group>
        <Divider mx="lg" color="gray.2" />
        <Text size="xs" c="dimmed" fw={700} mt="xl" mb="xs" px="lg" tt="uppercase" style={{ letterSpacing: '.1em' }}>
          Yönetim
        </Text>
        <Stack gap={3} px="sm">
          {PAGES.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.id}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => `re-nav ${isActive ? 're-nav-active' : ''}`}
              >
                {({ isActive }) => (
                  <>
                    <Icon size={19} />
                    <span className="re-nav-text">
                      <strong>{item.title}</strong>
                      <small>{item.subtitle}</small>
                    </span>
                    {isActive && <IconChevronRight size={15} className="re-nav-chevron" />}
                  </>
                )}
              </NavLink>
            );
          })}
        </Stack>
        <Box className="re-sidebar-bottom">
          <Divider mb="md" />
          <Text c="dimmed" size="xs" ta="center">Gayrimenkul Portföy Yönetimi</Text>
        </Box>
      </aside>

      {mobileOpen && (
        <button type="button" className="re-mobile-overlay" aria-label="Menüyü kapat" onClick={() => setMobileOpen(false)} />
      )}

      <div className="re-content">
        <Group justify="space-between" px="xl" className="re-topbar">
          <Group gap="sm" wrap="nowrap">
            <ActionIcon aria-label="Menüyü aç" className="re-menu-trigger" variant="subtle" color="gray" onClick={() => setMobileOpen(true)}>
              <IconMenu2 />
            </ActionIcon>
        <Group gap="sm">
          <Button
            component={Link}
            to="/"
            variant="light"
            color="blue"
            size="sm"
            radius="md"
            leftSection={<IconArrowLeft size={17} />}
          >
            Ana Portala Dön
          </Button>
        </Group>
          </Group>
        </Group>
        <main className="re-main">
          <Outlet context={{ api: realEstateApi, canEdit, refreshKey, onMutated, onNavigate }} />
        </main>
      </div>
    </Box>
  );
}
